import React from 'react';
import PropertyCard from './PropertyCard';

interface Property {
  id: number;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  latitude: number | null;
  longitude: number | null;
  name?: string;
  fk_business_id: number;
}

interface PropertiesListProps {
  properties: Property[];
  selectedProperties: number[];
  onPropertyToggle: (propertyId: number) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onViewResults: () => void;
  hasResults: boolean;
}

const PropertiesList: React.FC<PropertiesListProps> = ({
  properties,
  selectedProperties,
  onPropertyToggle,
  currentPage,
  totalPages,
  onPageChange,
  onViewResults,
  hasResults
}) => {
  const startIndex = (currentPage - 1) * 5;
  const endIndex = startIndex + 5;
  const currentPageProperties = properties.slice(startIndex, endIndex);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header with title and View Results button */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Properties ({properties.length})
        </h3>
        {hasResults && (
          <button
            onClick={onViewResults}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            View Results
          </button>
        )}
      </div>

      {/* Properties Table */}
      {properties.length > 0 ? (
        <>
          {/* Table Header */}
          <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
              <div className="col-span-1">Select</div>
              <div className="col-span-3">Address</div>
              <div className="col-span-2">City</div>
              <div className="col-span-1">State</div>
              <div className="col-span-2">Zip</div>
              <div className="col-span-2">Coordinates</div>
              <div className="col-span-1">Status</div>
            </div>
          </div>

          {/* Properties */}
          <div className="divide-y divide-gray-200">
            {currentPageProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                isSelected={selectedProperties.includes(property.id)}
                onToggle={() => onPropertyToggle(property.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {startIndex + 1} to {Math.min(endIndex, properties.length)} of {properties.length} properties
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="px-6 py-12 text-center">
          <div className="text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No properties found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Select a company and load properties to get started.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesList;
